import type { Conversation } from '@ralph/contracts'

/**
 * The sidebar's recency grouping.
 *
 * A flat list of conversations stops being scannable at about a dozen rows: every entry looks
 * equally recent, so finding "the one from this morning" means reading titles rather than
 * looking in a place. Grouping restores that place, and the group header carries a count so
 * the size of each bucket is legible without counting rows.
 *
 * Pinned comes first and is *exclusive* — a pinned conversation appears in its own group and
 * nowhere else. Listing it twice would make the counts lie, and the whole point of pinning is
 * that the row stops moving as time passes.
 *
 * Boundaries are calendar days in the viewer's own timezone, not fixed durations. "Today"
 * meaning the last 24 hours would put yesterday evening's conversation under Today at 9am,
 * which is not what the word says.
 *
 * `now` is a parameter rather than a `Date.now()` call inside so the grouping is a pure
 * function of its inputs: the boundary cases below are the ones worth testing, and they are
 * only testable if the caller owns the clock.
 *
 * **There is no `label` on a group, and its absence is load-bearing** (PRD-27 LOC-012, and
 * the shape of F-9). This module used to carry the five headings itself, which made a pure
 * date function own copy it could not re-read when the language changed — and a module-scope
 * object is exactly the capture that renders the previous language with no error anywhere.
 * The key is the whole answer: `shell.conversationGroups[key]` is the heading, looked up in
 * the component body, and deleting the field rather than merely leaving it unused is what
 * makes the compiler find any site that still reaches for a word from here.
 */

export type ConversationGroup = {
  /**
   * Stable across renders, so React keys do not churn when a row crosses midnight — and the
   * key the shell's catalogue uses to find this group's heading.
   */
  key: 'pinned' | 'today' | 'yesterday' | 'week' | 'earlier'
  items: Conversation[]
}

const startOfDay = (at: number): number => {
  const date = new Date(at)
  date.setHours(0, 0, 0, 0)
  return date.getTime()
}

export const groupConversations = (conversations: Conversation[], now: number): ConversationGroup[] => {
  const today = startOfDay(now)
  const yesterday = today - 86_400_000
  const week = today - 6 * 86_400_000

  const buckets: Record<ConversationGroup['key'], Conversation[]> = {
    pinned: [],
    today: [],
    yesterday: [],
    week: [],
    earlier: []
  }

  for (const conversation of conversations) {
    if (conversation.pinned) {
      buckets.pinned.push(conversation)
      continue
    }
    // An unparseable timestamp sorts as 0 and would silently sink to "Earlier". Treating it
    // as the oldest possible row is the honest failure: the conversation stays reachable,
    // just at the bottom, rather than disappearing from every group.
    const at = new Date(conversation.updatedAt).getTime()
    if (Number.isNaN(at)) buckets.earlier.push(conversation)
    else if (at >= today) buckets.today.push(conversation)
    else if (at >= yesterday) buckets.yesterday.push(conversation)
    else if (at >= week) buckets.week.push(conversation)
    else buckets.earlier.push(conversation)
  }

  const order: ConversationGroup['key'][] = ['pinned', 'today', 'yesterday', 'week', 'earlier']

  return order
    .map((key) => ({
      key,
      // Newest first inside a group. The API already sorts, but a client-side group that
      // trusted that would reorder itself the first time a row is updated in place.
      items: buckets[key].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    }))
    .filter((group) => group.items.length > 0)
}
